"use client";

import React, { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useWorkspace } from "@/lib/context/WorkspaceContext";
import { getViewerTier } from "@/lib/permissions";
import { fetchWithAuth, API_BASE } from "@/lib/auth";
import { 
    Shield, ClipboardList, Briefcase, ChevronRight, Award, 
    Layers, Users, Database, Clock, Calendar, CheckCircle, Flame,
    MapPin
} from "lucide-react";

import GigCard, { type GigPostingType } from "../../gigs/GigCard";
import GigFilters from "../../gigs/GigFilters";
import GigModal from "../../gigs/GigModal";
import RaiseHandModal from "../../gigs/RaiseHandModal";

export const CHAPTER_NAME_MAP: Record<string, string> = {
    "AD": "Andorra",
    "AE": "United Arab Emirates",
    "AF": "Afghanistan",
    "AG": "Antigua and Barbuda",
    "AL": "Albania",
    "AM": "Armenia",
    "AO": "Angola",
    "AR": "Argentina",
    "AT": "Austria",
    "AU": "Australia",
    "AZ": "Azerbaijan",
    "BA": "Bosnia & Herzegovina",
    "BB": "Barbados",
    "BD": "Bangladesh",
    "BE": "Belgium",
    "BF": "Burkina Faso",
    "BG": "Bulgaria",
    "BH": "Bahrain",
    "BJ": "Benin",
    "BL": "Saint Barthélemy",
    "BM": "Bermuda",
    "BN": "Brunei",
    "BO": "Bolivia",
    "BR": "Brazil",
    "BS": "The Bahamas",
    "BT": "Bhutan",
    "BW": "Botswana",
    "BY": "Belarus",
    "CA": "Canada",
    "CD": "DR Congo",
    "CF": "Central African Republic",
    "CG": "Congo",
    "CH": "Switzerland",
    "CI": "Ivory Coast",
    "CL": "Chile",
    "CM": "Cameroon",
    "CN": "China",
    "CO": "Colombia",
    "CR": "Costa Rica",
    "CV": "Cape Verde",
    "CW": "Curaçao",
    "CY": "Cyprus",
    "CZ": "Czechia",
    "DE": "Germany",
    "DJ": "Djibouti",
    "DK": "Denmark",
    "DO": "Dominican Republic",
    "DZ": "Algeria",
    "EC": "Ecuador",
    "EE": "Estonia",
    "EG": "Egypt",
    "EH": "Western Sahara",
    "ES": "Spain",
    "ET": "Ethiopia",
    "FI": "Finland",
    "FM": "Micronesia",
    "FR": "France",
    "GB": "Great Britain",
    "GE": "Georgia",
    "GF": "French Guiana",
    "GH": "Ghana",
    "GI": "Gibraltar",
    "GP": "Guadeloupe",
    "GQ": "Equatorial Guinea",
    "GR": "Greece",
    "GT": "Guatemala",
    "GU": "Guam",
    "GW": "Guinea-Bissau",
    "GY": "Guyana",
    "HK": "Hong Kong",
    "HN": "Honduras",
    "HR": "Croatia",
    "HT": "Haiti",
    "HU": "Hungary",
    "ID": "Indonesia",
    "IE": "Ireland",
    "IL": "Israel",
    "IM": "Isle of Man",
    "IN": "India",
    "IQ": "Iraq",
    "IR": "Iran",
    "IS": "Iceland",
    "IT": "Italy",
    "JE": "Jersey",
    "JM": "Jamaica",
    "JO": "Jordan",
    "JP": "Japan",
    "KE": "Kenya",
    "KG": "Kyrgyzstan",
    "KH": "Cambodia",
    "KN": "Saint Kitts and Nevis",
    "KP": "North Korea",
    "KR": "South Korea",
    "KW": "Kuwait",
    "KY": "Cayman Islands",
    "KZ": "Kazakhstan",
    "LA": "Laos",
    "LB": "Lebanon",
    "LI": "Liechtenstein",
    "LK": "Sri Lanka",
    "LS": "Lesotho",
    "LT": "Lithuania",
    "LU": "Luxembourg",
    "LV": "Latvia",
    "LY": "Libya",
    "MA": "Morocco",
    "MC": "Monaco",
    "MD": "Moldova",
    "ME": "Montenegro",
    "MF": "Saint Martin",
    "MG": "Madagascar",
    "MH": "Marshall Islands",
    "MK": "Macedonia",
    "ML": "Mali",
    "MM": "Burma",
    "MN": "Mongolia",
    "MO": "Macao",
    "MR": "Mauritania",
    "MT": "Malta",
    "MU": "Mauritius",
    "MV": "Maldives",
    "MW": "Malawi",
    "MX": "Mexico",
    "MY": "Malaysia",
    "MZ": "Mozambique",
    "NA": "Namibia",
    "NC": "New Caledonia",
    "NE": "Niger",
    "NG": "Nigeria",
    "NL": "Netherlands",
    "NO": "Norway",
    "NP": "Nepal",
    "NZ": "New Zealand",
    "OM": "Oman",
    "PA": "Panama",
    "PE": "Peru",
    "PG": "Papua New Guinea",
    "PH": "Philippines",
    "PK": "Pakistan",
    "PL": "Poland",
    "PR": "Puerto Rico",
    "PS": "Palestine",
    "PT": "Portugal",
    "PY": "Paraguay",
    "QA": "Qatar",
    "RE": "Réunion",
    "RO": "Romania",
    "RS": "Serbia",
    "RU": "Russia",
    "RW": "Rwanda",
    "SA": "Saudi Arabia",
    "SB": "Solomon Islands",
    "SD": "Sudan",
    "SE": "Sweden",
    "SG": "Singapore",
    "SI": "Slovenia",
    "SK": "Slovakia",
    "SL": "Sierra Leone",
    "SM": "San Marino",
    "SN": "Senegal",
    "SO": "Somalia",
    "SR": "Suriname",
    "SS": "South Sudan",
    "ST": "São Tomé and Príncipe",
    "SV": "El Salvador",
    "SX": "Sint Maarten",
    "SY": "Syria",
    "SZ": "Eswatini",
    "TG": "Togo",
    "TH": "Thailand",
    "TJ": "Tajikistan",
    "TL": "East Timor",
    "TM": "Turkmenistan",
    "TN": "Tunisia",
    "TR": "Turkey",
    "TT": "Trinidad and Tobago",
    "TW": "Taiwan",
    "TZ": "Tanzania",
    "UA": "Ukraine",
    "UG": "Uganda",
    "US": "United States",
    "UY": "Uruguay",
    "UZ": "Uzbekistan",
    "VA": "Vatican",
    "VC": "Saint Vincent and the Grenadines",
    "VE": "Venezuela",
    "VG": "British Virgin Islands",
    "VI": "U.S. Virgin Islands",
    "VN": "Vietnam",
    "VU": "Vanuatu",
    "XK": "Kosovo",
    "YE": "Yemen",
    "ZA": "South Africa",
    "ZM": "Zambia",
    "ZW": "Zimbabwe",
};

export const getChapterDisplayName = (chapter: string | null | undefined): string => {
    if (!chapter) return "";
    const clean = chapter.trim();
    const upper = clean.toUpperCase();
    if (CHAPTER_NAME_MAP[upper]) {
        return `${CHAPTER_NAME_MAP[upper]} Chapter`;
    }
    if (clean.toLowerCase().includes("chapter")) return clean;
    return `${clean} Chapter`;
};

const getCommunityDisplayName = (community: string | null | undefined): string => {
    if (!community) return "";
    const clean = community.trim();
    if (clean.toLowerCase().includes("community")) return clean;
    return `${clean} Community`;
};

export const formatJourney = (journey?: string | null): string | null => {
    if (!journey || journey === "..." || journey.toLowerCase() === "never a candidate" || journey.toLowerCase() === "never a member") return null;
    let clean = journey
        .split("_")
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    
    // Fix run-together candidate levels (e.g. Level1candidate -> Level 1 Candidate)
    clean = clean
        .replace(/Level1candidate/gi, "Level 1 Candidate")
        .replace(/Level2candidate/gi, "Level 2 Candidate")
        .replace(/Level3candidate/gi, "Level 3 Candidate")
        .replace(/3eligible/gi, "3 Eligible")
        .replace(/2eligible/gi, "2 Eligible")
        .replace(/1eligible/gi, "1 Eligible")
        .replace(/L1/g, "Level 1")
        .replace(/L2/g, "Level 2")
        .replace(/L3/g, "Level 3");
        
    return clean;
};

const getRecognitionInfo = (tier?: string | null) => {
    if (!tier) {
        return {
            label: "Recognized",
            bg: "bg-gray-50 border-gray-200 text-gray-500"
        };
    }
    const t = tier.toLowerCase();
    if (t === "tier_a" || t === "tiera" || t === "tier1") {
        return {
            label: "Private Service Record",
            bg: "bg-amber-50 border-amber-200 text-amber-700"
        };
    }
    if (t === "tier_b" || t === "tierb" || t === "tier2") {
        return {
            label: "Public Profile Eligible",
            bg: "bg-[#007a8a]/5 border-[#007a8a]/20 text-[#007a8a]"
        };
    }
    return {
        label: `${tier.replace(/tier/gi, "Tier ").replace(/_/g, " ")} Recognized`,
        bg: "bg-gray-50 border-gray-200 text-gray-500"
    };
};

export function GigsPanel() {
    const { user, isLoading: isUserLoading } = useWorkspace();
    const router = useRouter();
    const viewerTier = getViewerTier(user);

    // Dashboard states
    const [dashboardChapter, setDashboardChapter] = useState<string | null>(undefined as any);
    const [dashboardCommunity, setDashboardCommunity] = useState<string | null>(undefined as any);
    const [dashboardNearestCommunity, setDashboardNearestCommunity] = useState<string | null>(null);
    const [commitments, setCommitments] = useState<any[]>([]);
    const [pools, setPools] = useState<any[]>([]);
    const [history, setHistory] = useState<any[]>([]);
    const [bandA, setBandA] = useState<GigPostingType[]>([]);
    const [bandB, setBandB] = useState<GigPostingType[]>([]);
    const [isLoadingDashboard, setIsLoadingDashboard] = useState(true);

    // Resolve user chapter and community from dashboard geo fields (which derive from geo_assignments) with fallback
    const rawChapter = dashboardChapter !== undefined ? dashboardChapter : (user?.chapter || null);
    const rawCommunity = dashboardCommunity !== undefined ? dashboardCommunity : (user?.community || null);

    const resolvedChapter = rawChapter?.toLowerCase().includes("ineligible") ? null : rawChapter;
    const resolvedCommunity = rawCommunity?.toLowerCase().includes("unassigned") ? null : rawCommunity;
    // Nearest community is shown only when someone has a chapter but no assigned community
    const resolvedNearestCommunity = (!resolvedCommunity && resolvedChapter && dashboardNearestCommunity) ? dashboardNearestCommunity : null;

    const progJourney = formatJourney(user?.program_journey);
    const memJourney = formatJourney(user?.membership_journey);

    // Filter states
    const [searchQuery, setSearchQuery] = useState("");
    const [cadenceFilter, setCadenceFilter] = useState("all");

    // Collapsible states
    const [bandBOpen, setBandBOpen] = useState(false);

    // Modal state
    const [selectedPostingId, setSelectedPostingId] = useState<string | null>(null);
    const [isSelectedGigEligible, setIsSelectedGigEligible] = useState(true);

    const isStaffOrAdmin = viewerTier === "admin" || viewerTier === "staff";

    const fetchDashboardData = async () => {
        setIsLoadingDashboard(true);
        try {
            const res = await fetchWithAuth(`${API_BASE}/api/v1/gigs/me/dashboard`);
            if (res.ok) {
                const data = await res.json();
                setCommitments(data.commitments || []);
                setPools(data.pools || []);
                setHistory(data.history || []);
                setBandA(data.band_a || []);
                setBandB(data.band_b || []);
                setDashboardChapter(data.geo?.chapter || null);
                setDashboardCommunity(data.geo?.community || null);
                setDashboardNearestCommunity(data.geo?.nearest_community || null);
            }
        } catch (err) {
            console.error("Error fetching volunteer gigs dashboard:", err);
        } finally {
            setIsLoadingDashboard(false);
        }
    };

    useEffect(() => {
        if (isUserLoading || !user) return;
        fetchDashboardData();
    }, [user, isUserLoading]);

    if (isUserLoading || isLoadingDashboard) {
        return (
            <div className="py-16 flex flex-col items-center justify-center gap-3">
                <div className="w-8 h-8 border-2 border-[#007a8a]/20 border-t-[#007a8a] rounded-full animate-spin" />
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Loading Gigs Hub...</p>
            </div>
        );
    }

    // Apply client-side filters on Band A opportunities
    const filteredOpportunities = bandA.filter((gig) => {
        // Keyword filter (title, group, description)
        const matchesQuery = 
            gig.gig_type_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (gig.description || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
            gig.owning_entity_type.toLowerCase().includes(searchQuery.toLowerCase());

        // Cadence filter
        const matchesCadence = cadenceFilter === "all" || gig.cadence === cadenceFilter;
        return matchesQuery && matchesCadence;
    });

    return (
        <div className="space-y-8 animate-in fade-in duration-500 font-sans text-slate-800">
            {/* Administrative / Leader Shortcuts Panel */}
            {isStaffOrAdmin && (
                <div className="bg-gradient-to-r from-[#003d45] to-[#005c68] rounded-xl p-6 text-white border border-[#003d45]/20 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                        <Shield className="w-5 h-5 text-[#a9c4bb] shrink-0" />
                        <h3 className="text-xs font-black uppercase tracking-wider">Management & Operations</h3>
                    </div>
                    <p className="text-xs text-white/80 max-w-xl mb-5 leading-relaxed">
                        You have leadership or administrative access. Manage regional/group opportunities, triage volunteer registrations, and view participation audit trails.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            onClick={() => router.push("/dashboard/gigs/console")}
                            className="bg-[#007a8a] hover:bg-[#006977] text-white px-5 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all inline-flex items-center justify-center gap-2 shadow-lg shadow-black/15 border-none cursor-pointer"
                        >
                            <Database className="w-4 h-4" />
                            Operations Console
                        </button>
                        <button
                            onClick={() => router.push("/dashboard/gigs/create")}
                            className="bg-white/10 hover:bg-white/15 text-white border border-white/25 px-5 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all inline-flex items-center justify-center gap-2 cursor-pointer"
                        >
                            <Briefcase className="w-4 h-4 text-[#a9c4bb]" />
                            Create Opportunity
                        </button>
                    </div>
                </div>
            )}

            {/* Header Layout */}
            <div className="pb-4 border-b border-gray-150">
                <div className="space-y-2 max-w-2xl">
                    <span className="font-sans font-bold text-[11px] uppercase tracking-[0.16em] text-[#007a8a] block">
                        Your personal space
                    </span>
                    <h1 className="font-sans font-black text-3xl sm:text-4xl text-[#004d57] leading-none uppercase tracking-wide">
                        Gigs you can <span className="text-[#4b8892]">do now</span>
                    </h1>
                    <p className="text-sm text-gray-500 leading-relaxed">
                        This is your space to <strong>discover, learn, and pick up</strong> volunteer gigs. Every card is a single, well-defined task — nothing to manage, nothing to chase. Click any gig to read the full brief; it won't commit you to anything.
                    </p>
                </div>
            </div>

            {/* Main Content Grid: Left Feed, Right sticky column */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 items-start">
                
                {/* Left Area: Filters & Matched Gigs */}
                <div className="space-y-6">
                    {/* Filters */}
                    <GigFilters
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        cadenceFilter={cadenceFilter}
                        setCadenceFilter={setCadenceFilter}
                    />

                    {/* Band A eligible now list */}
                    <div className="space-y-4">
                        <div className="flex items-baseline gap-2">
                            <h2 className="font-sans font-bold text-lg text-[#004d57]">Eligible for you now</h2>
                            <span className="text-xs text-gray-400 font-semibold">{filteredOpportunities.length} gigs matched to you</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">
                            <span className="text-[#007a8a] font-bold">Click a card to learn more — you won't commit to anything.</span>
                        </p>

                        {filteredOpportunities.length === 0 ? (
                            <div className="bg-white border border-gray-150 rounded-xl p-12 text-center shadow-sm">
                                <div className="w-12 h-12 rounded-full bg-[#007a8a]/5 text-[#007a8a] flex items-center justify-center mx-auto mb-4">
                                    <ClipboardList className="w-6 h-6" />
                                </div>
                                <h4 className="text-sm font-bold text-[#004d57] uppercase tracking-wider">No matching opportunities</h4>
                                <p className="text-xs text-gray-400 mt-2 max-w-sm mx-auto leading-relaxed">
                                    We couldn't find any volunteer opportunities that match your current search terms or filters.
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                {filteredOpportunities.map((gig) => (
                                    <GigCard
                                        key={gig.id}
                                        gig={gig}
                                        onClick={() => {
                                            setSelectedPostingId(gig.id);
                                            setIsSelectedGigEligible(true);
                                        }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Band B collapsible section */}
                    <div id="where-you-could-grow" className="bg-white border border-gray-150 rounded-xl overflow-hidden shadow-sm">
                        <button 
                            onClick={() => setBandBOpen(!bandBOpen)}
                            className="w-full bg-[#a9c4bb] border-none text-left p-4 flex items-center gap-3 cursor-pointer hover:bg-[#97b3a9] transition-colors"
                        >
                            <span className="flex gap-1 items-end h-4 shrink-0">
                                <span className="w-1 h-[45%] bg-[#004d57] rounded-full" />
                                <span className="w-1 h-[75%] bg-[#004d57] rounded-full" />
                                <span className="w-1 h-[100%] bg-[#004d57] rounded-full" />
                            </span>
                            <div className="flex-1">
                                <h3 className="font-sans font-bold text-sm text-[#004d57] uppercase tracking-wide">Where you could grow</h3>
                                <p className="text-xs text-[#004d57]/80 mt-0.5 leading-normal">
                                    Gigs that open as your journey advances. Shown so you can see the path — not open to pick up yet.
                                </p>
                            </div>
                            <span className={`text-[#004d57] text-xs transition-transform duration-200 shrink-0 ${bandBOpen ? "rotate-180" : ""}`}>
                                ▼
                            </span>
                        </button>

                        {bandBOpen && (
                            <div className="p-[18px] animate-in fade-in slide-in-from-top-1 duration-200 border-t border-gray-100 bg-gray-50/50">
                                {bandB.length === 0 ? (
                                    <div className="text-center p-8 text-xs text-gray-400 font-semibold bg-white border border-gray-150 rounded-xl">
                                        No future opportunities identified yet.
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        {bandB.map((gig) => (
                                            <GigCard
                                                key={gig.id}
                                                gig={gig}
                                                onClick={() => {
                                                    setSelectedPostingId(gig.id);
                                                    setIsSelectedGigEligible(false);
                                                }}
                                                hasAccess={isStaffOrAdmin}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Area: Sticky rail for commitments & availability */}
                <div className="space-y-6 lg:sticky lg:top-[90px]">
                    {/* Standing Card Widget and location correction disclaimer grouped */}
                    <div className="space-y-2">
                        {/* Standing Card Widget */}
                        <div className="bg-white border border-gray-150 rounded-xl p-4 shadow-sm flex items-center gap-4 w-full">
                            <div className="flex-1">
                                <span className="font-sans font-bold text-[9px] uppercase tracking-[0.16em] text-[#007a8a] block">
                                    Your standing
                                </span>
                                <div className="font-bold text-sm text-[#004d57] mt-1 leading-snug">
                                    {progJourney || memJourney ? (
                                        <span>
                                            {progJourney && memJourney ? `${progJourney} / ${memJourney}` : (progJourney || memJourney)}
                                        </span>
                                    ) : (
                                        <span>
                                            {user?.designations?.includes("CMT") || user?.role?.toLowerCase() === "member" || user?.role?.toLowerCase() === "leader" || isStaffOrAdmin
                                                ? "CMT charterholder"
                                                : "GUEST"}
                                        </span>
                                    )}
                                </div>
                                {(resolvedChapter || resolvedCommunity || resolvedNearestCommunity) && (
                                    <div className="text-xs text-gray-400 mt-2 font-medium space-y-1.5">
                                        {resolvedChapter && (
                                            <div className="flex items-center gap-1.5 text-slate-600">
                                                <MapPin className="w-3.5 h-3.5 text-[#007a8a] shrink-0" />
                                                <span>{getChapterDisplayName(resolvedChapter)}</span>
                                            </div>
                                        )}
                                        {resolvedCommunity ? (
                                             <div className="flex items-center gap-1.5 text-slate-500">
                                                 <Users className="w-3.5 h-3.5 text-[#4b8892] shrink-0" />
                                                 <div>
                                                     <span>{getCommunityDisplayName(resolvedCommunity)}</span>
                                                     {dashboardNearestCommunity && 
                                                      dashboardNearestCommunity.toLowerCase().trim() !== resolvedCommunity.toLowerCase().trim() && (
                                                         <span className="text-gray-400 text-[10px] block font-normal italic mt-0.5">
                                                             Nearest to {getCommunityDisplayName(dashboardNearestCommunity)}
                                                         </span>
                                                     )}
                                                 </div>
                                             </div>
                                         ) : resolvedNearestCommunity ? (
                                             <div className="flex items-center gap-1.5 text-slate-400 italic">
                                                 <Users className="w-3.5 h-3.5 text-[#4b8892]/60 shrink-0" />
                                                 <span>Nearest to {getCommunityDisplayName(resolvedNearestCommunity)}</span>
                                             </div>
                                         ) : null}
                                    </div>
                                )}
                            </div>
                            <div className="w-[1px] self-stretch bg-gray-200 my-1" />
                            <div className="text-center shrink-0 w-[70px]">
                                <div className="font-sans font-bold text-2xl text-[#4b8892] leading-none">
                                    {commitments.length}
                                </div>
                                <div className="text-[9px] text-gray-400 mt-1 uppercase tracking-wider font-semibold leading-tight">
                                    gigs in progress
                                </div>
                            </div>
                        </div>
                        {/* Disclaimer note under the card */}
                        <div className="text-[10px] text-gray-400 px-1 leading-normal">
                            {resolvedChapter || resolvedCommunity || resolvedNearestCommunity
                                ? "If you think location details are incorrect, update your address in the MyCMT portal or contact Staff."
                                : "Unable to derive geographic data. Update your address in the MyCMT portal to match with more relevant gigs."}
                        </div>
                    </div>

                    {/* Commitments Card */}
                    <div className="bg-white border border-gray-150 rounded-xl p-[18px] shadow-sm">
                        <span className="font-sans font-bold text-[10px] uppercase tracking-[0.16em] text-[#007a8a] block">
                            My commitments
                        </span>
                        <span className="text-[11px] text-gray-400 mt-1 block">
                            Gigs you've already picked up
                        </span>

                        {commitments.length === 0 ? (
                            <div className="text-xs text-gray-400 py-4 italic leading-relaxed">
                                No active commitments. Pick up a gig from the marketplace!
                            </div>
                        ) : (
                            <div className="mt-3 divide-y divide-gray-100">
                                {commitments.map((c) => (
                                    <div 
                                        key={c.id}
                                        onClick={() => {
                                            setSelectedPostingId(c.gig_posting_id);
                                            setIsSelectedGigEligible(true);
                                        }}
                                        className="py-3 flex items-start gap-2.5 cursor-pointer group/item"
                                    >
                                        <span className="w-2 h-2 rounded-full bg-[#007a8a] shrink-0 mt-1.5" />
                                        <div>
                                            <div className="font-bold text-[13.5px] text-[#004d57] leading-snug group-hover/item:text-[#4b8892] transition-colors">
                                                {c.gig_posting_title}
                                            </div>
                                            <div className="text-[11.5px] text-gray-400 mt-0.5">
                                                {c.status.charAt(0).toUpperCase() + c.status.slice(1)}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                    </div>

                    {/* Standby pools card */}
                    <div className="bg-white border border-gray-150 rounded-xl p-[18px] shadow-sm">
                        <span className="font-sans font-bold text-[10px] uppercase tracking-[0.16em] text-[#007a8a] block">
                            Raise your hand
                        </span>
                        <p className="text-xs text-gray-500 leading-relaxed mt-2 mb-4">
                            Join a standby pool and we'll reach out when the right moment comes. No fixed commitment.
                        </p>

                        <div className="divide-y divide-gray-100">
                            {/* Dynamically list standby pools if loaded */}
                            {pools.map((p) => (
                                <div key={p.pool_id} className="py-3 flex items-center justify-between gap-4">
                                    <div>
                                        <div className="font-bold text-[13.5px] text-[#004d57] leading-snug">
                                            {p.gig_type_name}
                                        </div>
                                        {p.is_member && p.opted_in_at && (
                                            <div className="text-[11.5px] text-gray-400 mt-0.5 leading-normal">
                                                Registered: {new Date(p.opted_in_at).toLocaleDateString()}
                                            </div>
                                        )}
                                    </div>
                                    {p.is_member ? (
                                        <span className="text-[10px] font-sans font-bold text-[#004d57] bg-[#a9c4bb]/30 px-2 py-0.5 rounded-full uppercase tracking-wider shrink-0">
                                            Active
                                        </span>
                                    ) : (
                                        <RaiseHandModal
                                            poolId={p.pool_id}
                                            gigName={p.gig_type_name}
                                            description={p.description || ""}
                                            onSuccess={fetchDashboardData}
                                        >
                                            <button 
                                                className="bg-[#007a8a]/10 hover:bg-[#007a8a]/20 text-[#007a8a] font-sans font-bold text-[10px] uppercase tracking-wider py-1.5 px-3 rounded border-none cursor-pointer transition-colors shrink-0"
                                            >
                                                Raise Hand
                                            </button>
                                        </RaiseHandModal>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

            </div>

            {/* Contribution History cards */}
            {history.length > 0 && (
                <div id="contribution-history" className="space-y-4">
                    <span className="font-sans font-bold text-[10px] uppercase tracking-[0.16em] text-[#007a8a] block">
                        Your Contribution History
                    </span>
                    <div className="grid grid-cols-1 gap-3">
                        {history.map((h) => {
                            const info = getRecognitionInfo(h.recognition_tier);
                            const formattedDate = new Date(h.created_at).toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                            });
                            const isStaff = h.owning_entity_type?.toLowerCase() === "staff";
                            const entityLabel = isStaff ? "" : (h.owning_entity_name || (h.owning_entity_type 
                                ? (h.owning_entity_type.charAt(0).toUpperCase() + h.owning_entity_type.slice(1)) 
                                : ""));

                            return (
                                <div key={h.id} className="bg-white border border-gray-150 rounded-xl p-4 sm:p-5 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div>
                                        <h4 className="font-sans font-bold text-base text-[#004d57]">
                                            {h.gig_type_name}
                                        </h4>
                                        <p className="text-xs text-gray-500 mt-1 font-medium">
                                            Completed on {formattedDate} {entityLabel && `for ${entityLabel}`}
                                        </p>
                                    </div>
                                    <div className="shrink-0 flex items-center">
                                        <span className={`inline-flex items-center border px-3 py-1 rounded-full text-[9px] font-sans font-bold uppercase tracking-wider ${info.bg}`}>
                                            {info.label}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Details Modal */}
            {selectedPostingId && (
                <GigModal
                    postingId={selectedPostingId}
                    isEligible={isSelectedGigEligible}
                    onClose={() => setSelectedPostingId(null)}
                    onActionSuccess={() => {
                        setSelectedPostingId(null);
                        fetchDashboardData();
                        setBandBOpen(true);
                        setTimeout(() => {
                            const el = document.getElementById("where-you-could-grow");
                            if (el) {
                                el.scrollIntoView({ behavior: "smooth", block: "center" });
                            }
                        }, 250);
                    }}
                />
            )}
        </div>
    );
}

